import { Injectable, OnDestroy } from '@angular/core';

import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { ContactModel } from '../../../Common/TargetingShared/model/contact.model';
import { OutletModel } from '../models/outlet.model';
import { ContactApiModel, EntityApiModel, OutletApiModel } from '../../../api/targeting/model/targeting-search-result.api.model';
import { JournodexSubjectsService } from '../../../Common/Journodex/services/journodex-subjects.service';
import { EmploymentModel } from '../models/employment.model';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { BaseJournodexEntityModel } from '../models/base-journodex-entity.model';

@Injectable()
export class JournodexEntityFactory implements OnDestroy {

    private locale: string = 'en-US';
    private subscriptions: Subscription[] = [];

    constructor(
        private currentUserStateService: CurrentUserStateService,
        private subjectService: JournodexSubjectsService
    ) {
        this.subscriptions.push(
            this.currentUserStateService.getDataReferenceLanguageTag().pipe(
                filter(x => !!x)
            ).subscribe(languageTag => {
                this.locale = languageTag;
            })
        );
    }



    createEmptyBaseEntity(): BaseJournodexEntityModel {
        return new BaseJournodexEntityModel();
    }

    async createBaseEntity(apiModel: EntityApiModel): Promise<BaseJournodexEntityModel> {
        const entity = new BaseJournodexEntityModel();
        entity.subjects = await this.subjectService.createParentSubjectArrayFromApi(apiModel?.subjects);
        return entity;
    }

    createEmptyContact(): ContactModel {
        return new ContactModel();
    }

    async createContact(apiModel: ContactApiModel): Promise<ContactModel> {
        const entity = new ContactModel(apiModel);
        entity.subjects = await this.subjectService.createParentSubjectArrayFromApi(apiModel?.subjects);
        // Deal with apiModel.employments - avoid circular dep on employment factory
        const apiEmployments = apiModel.employments;
        if (apiEmployments) {
            const employmentPromises = apiEmployments.map(async apiEmployment => {
                const employement = new EmploymentModel(apiEmployment);
                if (apiEmployment?.outletInfo) {
                    employement.outlet = await this.createOutlet(apiEmployment.outletInfo);
                }
                return employement;
            });
            entity.employmentModels = await Promise.all(employmentPromises);
            if (entity.employmentModels.length) {
                entity.selectedEmployment = entity.employmentModels[0];
            }
        }
        return entity;
    }

    createEmptyOutlet(): OutletModel {
        return new OutletModel();
    }

    async createOutlet(apiModel: OutletApiModel): Promise<OutletModel> {
        const entity = new OutletModel(apiModel);
        entity.subjects = await this.subjectService.createParentSubjectArrayFromApi(apiModel?.subjects);
        return entity;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
